import { useEffect, useState } from "react";
import { docsClient, Export } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, Plus, FileText, Clock, CheckCircle, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import CreateExportDialog from "@/components/exports/CreateExportDialog";

export default function ExportsPage() {
  const [exports, setExports] = useState<Export[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadExports();
  }, []);

  const loadExports = async () => {
    try {
      const { data, error } = await supabase
        .from("exports")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      setExports(data || []);
    } catch (error) {
      console.error("Error loading exports:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: Export["status"]) => {
    switch (status) {
      case "complete":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "processing":
        return <Clock className="h-4 w-4 text-blue-600 animate-spin" />;
      case "pending":
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: Export["status"]) => {
    const variants: Record<Export["status"], "default" | "destructive" | "secondary" | "outline"> =
      {
        complete: "default",
        failed: "destructive",
        processing: "secondary",
        pending: "outline",
      };

    return (
      <Badge variant={variants[status]} className="capitalize">
        {status}
      </Badge>
    );
  };

  const getFormatBadge = (format: Export["format"]) => {
    const labels: Record<Export["format"], string> = {
      toc: "TOC",
      claude: "Claude",
      json: "JSON",
    };

    return (
      <Badge variant="outline" className="font-mono">
        {labels[format]}
      </Badge>
    );
  };

  const handleDownload = async (exportItem: Export) => {
    if (!exportItem.file_url) {
      toast({
        title: "Error",
        description: "No file available for download",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get signed URL for download
      const { data, error } = await docsClient.storage
        .from("exports")
        .createSignedUrl(exportItem.file_url, 3600); // 1 hour expiry

      if (error) throw error;

      // Open download in new tab
      window.open(data.signedUrl, "_blank");

      toast({
        title: "Success",
        description: "Download started",
      });
    } catch (error) {
      console.error("Error downloading export:", error);
      toast({
        title: "Error",
        description: "Failed to download export",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Exports</h1>
          <p className="text-muted-foreground mt-2">
            Create and download documentation exports in various formats
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Export
        </Button>
      </div>

      {/* Exports Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Format</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Documents</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Loading skeletons
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                </TableRow>
              ))
            ) : exports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="h-12 w-12 text-muted-foreground" />
                    <p className="text-muted-foreground">
                      No exports yet. Click "Create Export" to get started.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              exports.map((exportItem) => (
                <TableRow key={exportItem.id}>
                  <TableCell className="font-medium">{exportItem.name}</TableCell>
                  <TableCell>{getFormatBadge(exportItem.format)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {exportItem.tags && exportItem.tags.length > 0 ? (
                        exportItem.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-sm">All documents</span>
                      )}
                      {exportItem.tags && exportItem.tags.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{exportItem.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {exportItem.document_count}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(exportItem.status)}
                      {getStatusBadge(exportItem.status)}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(exportItem.created_at), {
                      addSuffix: true,
                    })}
                  </TableCell>
                  <TableCell>
                    {exportItem.status === "complete" && exportItem.file_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(exportItem)}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    )}
                    {exportItem.status === "failed" && (
                      <span className="text-xs text-destructive">Failed</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Export Dialog */}
      <CreateExportDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onExportCreated={loadExports}
      />
    </div>
  );
}
